import Agilite from 'agilite'
import store from '../../store'
import { pdfGenerationEnums } from '../Medical History/utils/diagnosis-utils'
import reportGenerationEnums from '../Admin/Reporting/reporting-enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const getInvoices = (filter = {}, page, pageLimit, projection = '') => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('invoices', 'read', {
          filter: JSON.stringify(filter),
          page,
          pageLimit,
          projection
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const invoicesAggregate = (pipeline = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute('invoices', 'aggregate', {
          pipeline: JSON.stringify(pipeline)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const countInvoices = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('invoices', 'count', {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createInvoice = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get patient's billingId if not provided
        let billingId = data.billingId

        if (!billingId && data.userRef) {
          try {
            // Import auth utils to get patient data
            const { findAgiliteUser } = await import('../Auth/utils/utils')
            const patient = await findAgiliteUser({ _id: data.userRef })

            // Check both possible locations for billingId
            if (patient?.billingId) {
              // billingId at root level (as stored in auth_users)
              billingId = patient.billingId
            } else if (patient?.extraData?.billingDetails?.billingId) {
              // billingId in extraData (legacy location)
              billingId = patient.extraData.billingDetails.billingId
            } else {
              // This should not happen - all patients should have a billingId
              throw new Error('Patient does not have a billingId. Please contact support.')
            }
          } catch (err) {
            console.error('Error fetching patient billingId:', err)
            throw new Error(
              'Could not retrieve patient billing information. Please ensure the patient profile is complete.'
            )
          }
        }

        data = {
          ...data,
          billingId: billingId,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('invoices', 'create', {
          data: JSON.stringify(data)
        })

        // After invoice creation, create corresponding ledger entry
        try {
          // Import the billing integration function dynamically to avoid circular dependencies
          const { processInvoiceCreation } = await import(
            '../Billing-Module-Enhanced/backend-integration/api/billingIntegration'
          )

          // Get billing records for this invoice to calculate amounts
          const { readBillingRecords } = await import('../Bookings/utils/utils')
          console.log('Fetching billing records for bookingRef:', data.bookingRef)
          const billingRecords = await readBillingRecords({ bookingRef: data.bookingRef })
          console.log('Billing records fetched:', {
            count: billingRecords?.length || 0,
            records: billingRecords
          })

          // Process invoice in billing system
          await processInvoiceCreation({
            ...response.data,
            billingRecords
          })
        } catch (billingError) {
          console.error('Failed to create billing ledger entry:', billingError)
          // Don't fail the invoice creation if billing integration fails
          // This ensures the invoice is still created even if billing module has issues
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const updateInvoice = (recordId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // If updating and billingId is not provided, try to get it from patient
        if (!data.billingId && data.userRef) {
          try {
            const { findAgiliteUser } = await import('../Auth/utils/utils')
            const patient = await findAgiliteUser({ _id: data.userRef })

            if (patient?.extraData?.billingDetails?.billingId) {
              data.billingId = patient.extraData.billingDetails.billingId
            }
          } catch (err) {
            console.warn('Could not fetch patient billingId for update:', err)
          }
        }

        // Get current invoice data to check for status changes
        const currentInvoiceResponse = await agilite.Connectors.execute('invoices', 'read_by_id', {
          recordId
        })
        const currentInvoice = currentInvoiceResponse.data

        const response = await agilite.Connectors.execute('invoices', 'update', {
          recordId,
          data: JSON.stringify(data)
        })

        // Check if invoice status changed to private and handle transition if needed
        if (currentInvoice && response.data) {
          const wasPrivate =
            currentInvoice.accountGroup === 'private' ||
            currentInvoice.goodXStatus === 'Private' ||
            !currentInvoice.goodXTicketNo

          const isNowPrivate =
            data.accountGroup === 'private' ||
            data.goodXStatus === 'Private' ||
            data.goodXTicketNo === null ||
            data.goodXTicketNo === ''

          // If invoice wasn't private before but is now, trigger transition
          if (!wasPrivate && isNowPrivate) {
            try {
              const { transitionInvoiceToPrivate } = await import(
                '../Billing-Module-Enhanced/backend-integration/api/invoiceStatusTransition'
              )
              await transitionInvoiceToPrivate(recordId, 'manual_change')
              console.log(`Invoice ${currentInvoice.invoiceNo} transitioned to private due to status update`)
            } catch (transitionError) {
              console.error(`Failed to transition invoice ${currentInvoice.invoiceNo} to private:`, transitionError)
              // Don't fail the update if transition fails
            }
          }
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createPatientPayment = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('patient_payments', 'create', {
          data: JSON.stringify(data)
        })

        // After payment creation, update patient ledger
        try {
          // Import the billing integration function dynamically
          const { processPatientPayment } = await import(
            '../Billing-Module-Enhanced/backend-integration/api/billingIntegration'
          )

          // Get invoice details for the payment
          const invoices = await getInvoices({ _id: data.invoiceRef })
          const invoice = invoices && invoices.length > 0 ? invoices[0] : null

          // Process payment in billing system
          await processPatientPayment({
            ...data,
            invoiceNo: invoice?.invoiceNo,
            patientId: data.userRef
          })
        } catch (billingError) {
          console.error('Failed to update billing ledger for payment:', billingError)
          // Don't fail the payment creation if billing integration fails
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getPatientPayments = (filter, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('patient_payments', 'read', {
          filter: JSON.stringify(filter),
          page,
          pageLimit
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getInvoicesWithBillingRecords = (filter = {}, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const skip = page && pageLimit ? (page - 1) * pageLimit : 0
        const limit = pageLimit || 100

        // Debug: Log the collection name being used
        const collectionName = `${process.env.REACT_APP_AGILITE_TEAM_ID}_billing_records`
        console.log('Looking up billing records from collection:', collectionName)

        const pipeline = [
          { $match: filter },
          { $sort: { createdAt: -1 } },
          { $skip: skip },
          { $limit: limit },
          {
            // Add a field to ensure bookingRef is a string for consistent comparison
            $addFields: {
              bookingRefString: { $toString: '$bookingRef' }
            }
          },
          {
            $lookup: {
              from: collectionName,
              let: { bookingRef: '$bookingRefString' },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $eq: [{ $toString: '$bookingRef' }, '$$bookingRef']
                    }
                  }
                }
              ],
              as: 'billingRecords'
            }
          }
        ]

        const response = await agilite.Connectors.execute('invoices', 'aggregate', {
          pipeline: JSON.stringify(pipeline)
        })

        // Debug: Log first invoice to check billing records
        if (response.data && response.data.length > 0) {
          console.log('First invoice billing records:', response.data[0].billingRecords)
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getRemittances = (filter, page, pageSize) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('medical_aid_remittances', 'read', {
          filter: JSON.stringify(filter),
          page,
          pageSize
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createRemittance = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('medical_aid_remittances', 'create', {
          data: JSON.stringify(data)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const updateRemittance = (recordId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute('medical_aid_remittances', 'update', {
          recordId,
          data: JSON.stringify(data)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const countRemittances = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute('medical_aid_remittances', 'count', {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const generateCraftMyPdfReport = (payload, templateId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let pdfResponse = null
      try {
        pdfResponse = await agilite.Connectors.execute(
          pdfGenerationEnums.pdfGeneration.CRAFT_MY_PDF,
          reportGenerationEnums.routes.CREATE,
          {
            data: JSON.stringify({ data: payload }),
            templateId: JSON.stringify(templateId)
          }
        )

        if (pdfResponse.data && pdfResponse.data.status === 'success') {
          resolve(pdfResponse.data.file)
        } else {
          reject('Error generating PDF')
        }
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const deleteAllInvoices = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL invoices without any filter
        const filter = {}

        const response = await agilite.Connectors.execute('invoices', 'delete_many', {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const deleteAllRemittances = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL remittances without any filter
        const filter = {}

        const response = await agilite.Connectors.execute('medical_aid_remittances', 'delete_many', {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Search invoices by invoice number or patient name
 * @param {Object} params - Search parameters
 * @param {string} params.invoiceNo - Invoice number to search
 * @param {string} params.patientName - Patient name to search
 * @returns {Promise<Array>} Array of matching invoices
 */
export const searchInvoices = ({ invoiceNo, patientName }) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const filter = {
          entityRef: store.getState().core.entity._id
        }

        if (invoiceNo) {
          filter.invoiceNo = { $regex: invoiceNo, $options: 'i' }
        }

        if (patientName) {
          filter.patientName = { $regex: patientName, $options: 'i' }
        }

        const response = await agilite.Connectors.execute('invoices', 'read', {
          filter: JSON.stringify(filter),
          pageLimit: 50
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
